import { useVoiceSendingTransition } from "./use-voice-sending-transition";
import { useVoiceWakeStatus } from "./voice-wake-store";
import { useCallback, useEffect, useRef, useState } from "react";
import { ensureVoiceGlassKeyframes } from "@/components/cursors/voice-glass-keyframes";
import type { VoiceGradientStatus } from "@/components/cursors/voice-gradient-style";
import { useContinuousVoice } from "./continuous-voice-store";
import { useDictation } from "./use-dictation";
import { useTtsPlaybackStore } from "./tts-playback-store";
import { useVoiceIndicatorHidden } from "./voice-indicator-visibility";
import { voiceChatCommandLabel } from "./voice-control-intents";
import {
  useVoiceCommandFeedback,
  VOICE_COMMAND_FEEDBACK_MS,
} from "./voice-command-feedback-store";
import { useT } from "@/i18n";
import { compactMessages } from "@/i18n/messages/compact";

export const COMPACT_VOICE_SENT_PULSE_MS = 1_200;

export type CompactVoicePhase =
  | "starting"
  | "live"
  | "listening"
  | "sending"
  | "sent"
  | "speaking"
  | "error";

export function useCompactVoiceStatus() {
  const t = useT(compactMessages);
  const wakePhase = useVoiceWakeStatus((state) => state.phase);
  const wakeError = useVoiceWakeStatus((state) => state.error);
  const wakeVisible = ["sleeping", "waking", "error"].includes(wakePhase);
  const dictationStatus = useDictation((state) => state.status);
  const dictationCommandLabel = useDictation((state) =>
    voiceChatCommandLabel(state.partialText ?? ""),
  );
  const dictationErrorLabel = useDictation((state) => state.lastError);
  const deadMic = useDictation((state) => state.deadMic);
  const screenshotIntent = useDictation((state) => state.screenshotIntent);
  const continuousEnabled = useContinuousVoice((state) => state.enabled);
  const continuousStatus = useContinuousVoice((state) => state.status);
  const continuousCommandLabel = useContinuousVoice((state) =>
    voiceChatCommandLabel(state.partialText),
  );
  const continuousErrorLabel = useContinuousVoice((state) => state.lastError);
  const lastDispatchedAt = useContinuousVoice(
    (state) => state.lastDispatchedAt,
  );
  const speaking = useTtsPlaybackStore(
    (state) => state.speakingState === "started",
  );
  const suppressed = useVoiceIndicatorHidden();
  const feedbackLabel = useVoiceCommandFeedback((state) => state.label);
  const feedbackAt = useVoiceCommandFeedback((state) => state.announcedAt);
  const [feedbackVisible, setFeedbackVisible] = useState(false);
  const [sentPulse, setSentPulse] = useState(false);
  const [showLabel, setShowLabel] = useState(false);
  const labelTimerRef = useRef<number | null>(null);
  const sentTimerRef = useRef<number | null>(null);
  const hasError =
    wakePhase === "error" ||
    dictationStatus === "error" ||
    deadMic ||
    (continuousEnabled && continuousStatus === "error");
  const sending =
    (!continuousEnabled && dictationStatus === "processing") ||
    (continuousEnabled &&
      (continuousStatus === "finalizing" ||
        continuousStatus === "dispatching" ||
        continuousStatus === "queued"));
  const liveSpeech =
    continuousEnabled &&
    (continuousStatus === "speech" || continuousStatus === "pause_pending");
  const active =
    !suppressed &&
    (wakeVisible ||
      dictationStatus === "recording" ||
      dictationStatus === "processing" ||
      dictationStatus === "error" ||
      continuousEnabled ||
      speaking ||
      sentPulse ||
      feedbackVisible);
  const rawPhase: CompactVoicePhase = hasError
    ? "error"
    : continuousEnabled && continuousStatus === "starting"
      ? "starting"
      : speaking
        ? "speaking"
        : sending
          ? "sending"
          : sentPulse && !continuousEnabled
            ? "sent"
            : continuousEnabled && !liveSpeech
              ? "live"
              : "listening";
  const phase = useVoiceSendingTransition(rawPhase, continuousEnabled, "live");
  const recognizedCommandLabel =
    phase === "listening" || phase === "sending" || phase === "live"
      ? continuousEnabled
        ? continuousCommandLabel
        : dictationStatus === "recording" || dictationStatus === "processing"
          ? dictationCommandLabel
          : null
      : null;
  const commandLabel =
    (wakePhase === "sleeping"
      ? t("sleeping")
      : wakePhase === "waking"
        ? t("preparingMicrophone")
        : null) ??
    recognizedCommandLabel ??
    (feedbackVisible &&
    !liveSpeech &&
    (continuousEnabled || dictationStatus === "idle") &&
    (phase === "listening" || phase === "live")
      ? feedbackLabel
      : null);
  let status: VoiceGradientStatus = "listening";

  if (phase === "error") {
    status = "error";
  } else if (phase === "speaking") {
    status = "speaking";
  } else if (phase === "starting" || phase === "sending" || phase === "sent") {
    status = "processing";
  }

  useEffect(() => {
    const remaining = VOICE_COMMAND_FEEDBACK_MS - (Date.now() - feedbackAt);
    if (!feedbackLabel || remaining <= 0) {
      setFeedbackVisible(false);
      return;
    }
    setFeedbackVisible(true);
    const timer = window.setTimeout(() => setFeedbackVisible(false), remaining);
    return () => window.clearTimeout(timer);
  }, [feedbackAt, feedbackLabel]);

  useEffect(() => {
    if (sentTimerRef.current !== null) {
      window.clearTimeout(sentTimerRef.current);
      sentTimerRef.current = null;
    }
    if (typeof lastDispatchedAt !== "number") {
      setSentPulse(false);
      return;
    }
    const remaining =
      COMPACT_VOICE_SENT_PULSE_MS - Math.max(0, Date.now() - lastDispatchedAt);
    if (remaining <= 0) {
      setSentPulse(false);
      return;
    }
    setSentPulse(true);
    sentTimerRef.current = window.setTimeout(() => {
      setSentPulse(false);
      sentTimerRef.current = null;
    }, remaining);
  }, [lastDispatchedAt]);

  useEffect(() => {
    if (!liveSpeech || !sentPulse) return;
    if (sentTimerRef.current !== null) {
      window.clearTimeout(sentTimerRef.current);
      sentTimerRef.current = null;
    }
    setSentPulse(false);
  }, [liveSpeech, sentPulse]);

  useEffect(() => {
    ensureVoiceGlassKeyframes();
  }, []);

  const revealLabel = useCallback(() => {
    if (labelTimerRef.current !== null) {
      window.clearTimeout(labelTimerRef.current);
    }
    setShowLabel(true);
    labelTimerRef.current = window.setTimeout(() => {
      setShowLabel(false);
      labelTimerRef.current = null;
    }, 2200);
  }, []);

  const dismissLabel = useCallback(() => {
    if (labelTimerRef.current !== null) {
      window.clearTimeout(labelTimerRef.current);
      labelTimerRef.current = null;
    }
    setShowLabel(false);
  }, []);

  useEffect(() => {
    if (!active) {
      dismissLabel();
      return;
    }
    if (!continuousEnabled) revealLabel();
  }, [active, continuousEnabled, dismissLabel, revealLabel]);

  const previousAnnouncedPhaseRef = useRef(phase);
  useEffect(() => {
    if (
      active &&
      !continuousEnabled &&
      (phase === "error" || phase === "speaking") &&
      previousAnnouncedPhaseRef.current !== phase
    ) {
      revealLabel();
    }
    previousAnnouncedPhaseRef.current = phase;
  }, [active, continuousEnabled, phase, revealLabel]);

  useEffect(
    () => () => {
      if (labelTimerRef.current !== null) {
        window.clearTimeout(labelTimerRef.current);
      }
      if (sentTimerRef.current !== null) {
        window.clearTimeout(sentTimerRef.current);
      }
    },
    [],
  );

  return {
    active,
    commandLabel,
    errorLabel:
      phase !== "error"
        ? undefined
        : wakePhase === "error"
          ? wakeError
          : continuousEnabled && continuousStatus === "error"
            ? continuousErrorLabel
            : dictationStatus === "error"
              ? dictationErrorLabel
              : deadMic
                ? t("noMicrophoneInput")
                : undefined,
    live: continuousEnabled,
    phase,
    screenshotIntent,
    showLabel:
      (active && continuousEnabled) ||
      phase === "starting" ||
      showLabel ||
      Boolean(commandLabel),
    status,
  };
}

export type CompactVoiceStatus = ReturnType<typeof useCompactVoiceStatus>;
